(() => {
  'use strict';
  if (window.shkCardPreviewReady) return;
  window.shkCardPreviewReady = true;
  const selector = 'a[data-shk-card-preview]';
  const states = new WeakMap(), watched = new WeakSet(), active = new Set();
  const TAP_MAX_MS = 280, TAP_SLOP = 9, SWIPE_PX = 32;
  let gesture = null;

  function stateFor(target) {
    const link = target instanceof Element && target.closest(selector);
    if (!link) return null;
    let state = states.get(link);
    if (!state) {
      const alt = link.querySelector('img.ProductItem__Image--alternate');
      if (!alt) return null;
      state = { link, alt, ready: false, requested: false, hover: false, contact: false, pinned: false, blockUntil: 0, lastTouch: -Infinity };
      states.set(link, state);
    }
    return state;
  }

  function render(state) {
    if (!state) return;
    const wanted = state.hover || state.contact || state.pinned;
    state.link.toggleAttribute('data-shk-preview-active', Boolean(wanted && state.ready));
    if (wanted) active.add(state); else active.delete(state);
  }

  function load(state, urgent = false) {
    const img = state.alt;
    if (urgent) img.fetchPriority = 'high';
    if (state.requested) return;
    const source = img.getAttribute('data-shk-preview-src');
    if (!source) return;
    state.requested = true;
    if (!urgent) img.fetchPriority = 'low';
    const widths = (img.getAttribute('data-widths') || '').match(/\d+/g)?.map(Number) || [400, 600, 800, 1200];
    const needed = state.link.clientWidth * Math.min(window.devicePixelRatio || 1, 2);
    const width = widths.find(value => value >= needed) || widths[widths.length - 1];
    const loaded = async () => {
      img.removeEventListener('error', failed);
      try { if (img.decode) await img.decode(); } catch (_) {}
      state.ready = img.naturalWidth > 0;
      render(state);
    };
    const failed = () => {
      img.removeEventListener('load', loaded);
      state.requested = false;
      state.ready = false;
      render(state);
    };
    img.addEventListener('load', loaded, { once: true });
    img.addEventListener('error', failed, { once: true });
    img.src = source.replace('{width}', String(width));
  }

  function contactAt(x, y, target) {
    const g = gesture;
    if (!g) return;
    g.lastX = x; g.lastY = y;
    const next = stateFor(target || document.elementFromPoint(x, y));
    if (next === g.current) return;
    if (g.current) { g.current.contact = false; render(g.current); }
    if (next && next !== g.origin) g.crossed = true;
    g.current = next;
    if (next) {
      next.contact = true; next.lastTouch = performance.now();
      g.touched.add(next);
      load(next, true); render(next);
    }
  }

  function finish(cancelled = false) {
    if (!gesture) return;
    const g = gesture;
    if (cancelled || g.moved || performance.now() - g.started >= TAP_MAX_MS) {
      for (const state of g.touched) state.blockUntil = performance.now() + 800;
    }
    if (!cancelled && g.mode === 'horizontal' && !g.crossed && g.current === g.origin && g.origin && Math.abs(g.lastX - g.x) >= SWIPE_PX) {
      g.origin.pinned = !g.wasPinned;
    }
    if (g.current) { g.current.contact = false; render(g.current); }
    gesture = null;
  }

  function begin(event, x, y, kind, id) {
    finish(true);
    const state = stateFor(event.target);
    if (state) state.blockUntil = 0;
    gesture = { origin: state, current: null, touched: new Set(), id, kind, x, y, lastX: x, lastY: y, started: performance.now(), wasPinned: state?.pinned || false, moved: false, crossed: false, mode: 'pending' };
    contactAt(x, y, event.target);
  }

  function move(x, y) {
    const g = gesture;
    if (!g) return;
    const ax = Math.abs(x - g.x), ay = Math.abs(y - g.y);
    if (Math.hypot(ax, ay) > TAP_SLOP) g.moved = true;
    if (g.mode === 'pending') {
      if (ay > 10 && ay >= ax) g.mode = 'vertical';
      else if (ax > 14 && ax > ay * 1.35) g.mode = 'horizontal';
    }
    contactAt(x, y);
  }

  document.addEventListener('pointerover', event => {
    if (event.pointerType !== 'mouse') return;
    const state = stateFor(event.target);
    if (!state || (event.relatedTarget instanceof Node && state.link.contains(event.relatedTarget))) return;
    state.hover = true; load(state, true); render(state);
  });
  document.addEventListener('pointerout', event => {
    if (event.pointerType !== 'mouse') return;
    const state = stateFor(event.target);
    if (!state || (event.relatedTarget instanceof Node && state.link.contains(event.relatedTarget))) return;
    state.hover = false; render(state);
  });
  document.addEventListener('pointerdown', event => {
    if (event.pointerType === 'mouse') return;
    if (!event.isPrimary) { finish(true); return; }
    if (event.button === 0) begin(event, event.clientX, event.clientY, event.pointerType, event.pointerId);
  }, { passive: true });
  document.addEventListener('pointermove', event => {
    if (gesture && gesture.id === event.pointerId) move(event.clientX, event.clientY);
  }, { passive: true });
  document.addEventListener('pointerup', event => {
    if (gesture && gesture.kind !== 'touch' && gesture.id === event.pointerId) finish();
  });
  document.addEventListener('pointercancel', event => {
    if (gesture && gesture.kind !== 'touch' && gesture.id === event.pointerId) finish(true);
  });
  document.addEventListener('touchstart', event => {
    if (event.touches.length !== 1) { finish(true); return; }
    const touch = event.touches[0];
    if (!gesture || gesture.kind !== 'touch') begin(event, touch.clientX, touch.clientY, 'touch', null);
    gesture.touchId = touch.identifier;
  }, { passive: true });
  document.addEventListener('touchmove', event => {
    if (!gesture || gesture.kind !== 'touch') return;
    if (event.touches.length !== 1) { finish(true); return; }
    const touch = event.touches[0];
    if (touch.identifier === gesture.touchId) move(touch.clientX, touch.clientY);
  }, { passive: true });
  document.addEventListener('touchend', () => { if (gesture?.kind === 'touch') finish(); }, { passive: true });
  document.addEventListener('touchcancel', () => { if (gesture?.kind === 'touch') finish(true); }, { passive: true });
  document.addEventListener('scroll', () => {
    if (!gesture) return;
    gesture.moved = true; gesture.mode = 'vertical';
    contactAt(gesture.lastX, gesture.lastY);
  }, { passive: true, capture: true });
  document.addEventListener('click', event => {
    const state = stateFor(event.target);
    if (state && event.detail !== 0 && performance.now() < state.blockUntil) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('contextmenu', event => {
    const state = stateFor(event.target);
    if (state && (gesture?.touched.has(state) || performance.now() - state.lastTouch < 1200)) event.preventDefault();
  });
  for (const eventName of ['dragstart', 'selectstart']) {
    document.addEventListener(eventName, event => { if (stateFor(event.target)) event.preventDefault(); });
  }

  function reset() {
    finish(true);
    for (const state of active) {
      state.hover = state.contact = state.pinned = false;
      render(state);
    }
  }
  window.addEventListener('blur', reset);
  window.addEventListener('pagehide', reset);
  document.addEventListener('visibilitychange', () => { if (document.hidden) reset(); });

  const nearby = new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) {
      nearby.unobserve(entry.target);
      const state = stateFor(entry.target);
      if (state) load(state);
    }
  }, { rootMargin: '900px 0px' });
  function watch(root) {
    const links = root instanceof Element && root.matches(selector) ? [root] : Array.from(root.querySelectorAll?.(selector) || []);
    for (const link of links) {
      if (watched.has(link)) continue;
      watched.add(link);
      const state = stateFor(link);
      if (!state) continue;
      const box = link.getBoundingClientRect();
      if (box.width && box.bottom > 0 && box.top < innerHeight) load(state);
      else nearby.observe(link);
    }
  }
  function init() {
    watch(document);
    new MutationObserver(records => {
      for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) watch(node);
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
